import * as THREE from 'three';
import { PREMIUM_ROUTE } from '../utils/constants';

export type PremiumRouteGuidanceHandle = {
  group: THREE.Group;
  update: (
    dt: number,
    playerX: number,
    playerZ: number,
    extractionX: number,
    extractionZ: number,
    extractionUnlocked: boolean,
  ) => void;
};

function createChevronGeometry(): THREE.ShapeGeometry {
  const w = PREMIUM_ROUTE.CHEVRON_WIDTH * 0.5;
  const l = PREMIUM_ROUTE.CHEVRON_LENGTH * 0.5;
  const notch = l * 0.18;
  const shape = new THREE.Shape();
  shape.moveTo(0, l);
  shape.lineTo(-w, -l);
  shape.lineTo(0, -l + notch);
  shape.lineTo(w, -l);
  shape.lineTo(0, l);
  return new THREE.ShapeGeometry(shape);
}

/**
 * Route guidance is intentionally decorative and approximate. It points the eye
 * toward extraction without adding pathfinding or gameplay weight.
 */
export function createPremiumRouteGuidance(): PremiumRouteGuidanceHandle {
  const group = new THREE.Group();
  group.name = 'PremiumRouteGuidance';

  const chevronGeo = createChevronGeometry();
  const chevrons: THREE.Mesh[] = [];
  const chevronMat = new THREE.MeshBasicMaterial({
    color: 0x42f6ff,
    transparent: true,
    opacity: 0.46,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  for (let i = 0; i < PREMIUM_ROUTE.CHEVRON_COUNT; i++) {
    const mesh = new THREE.Mesh(chevronGeo, chevronMat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 8;
    group.add(mesh);
    chevrons.push(mesh);
  }

  const beamMat = new THREE.MeshBasicMaterial({
    color: 0x22ffbb,
    transparent: true,
    opacity: 0.105,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(
      PREMIUM_ROUTE.BEAM_RADIUS,
      PREMIUM_ROUTE.BEAM_RADIUS * 0.7,
      PREMIUM_ROUTE.BEAM_HEIGHT,
      32,
      1,
      true,
    ),
    beamMat,
  );
  beam.position.y = PREMIUM_ROUTE.BEAM_HEIGHT * 0.5;
  group.add(beam);

  const rings: THREE.Mesh[] = [];
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0x7dffe0,
    transparent: true,
    opacity: 0.32,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  for (let i = 0; i < PREMIUM_ROUTE.RING_COUNT; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(PREMIUM_ROUTE.BEAM_RADIUS + i * 1.9, 0.12, 8, 64),
      ringMat,
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 1.1 + i * 6.5;
    group.add(ring);
    rings.push(ring);
  }

  let time = 0;
  const cActive = new THREE.Color(0x46f7ff);
  const cUnlocked = new THREE.Color(0x2dff9b);
  const cLocked = new THREE.Color(0xff4fae);

  const update = (
    dt: number,
    playerX: number,
    playerZ: number,
    extractionX: number,
    extractionZ: number,
    extractionUnlocked: boolean,
  ) => {
    time += dt;
    const dx = extractionX - playerX;
    const dz = extractionZ - playerZ;
    const dist = Math.hypot(dx, dz);
    const dirX = dist > 0.001 ? dx / dist : 0;
    const dirZ = dist > 0.001 ? dz / dist : 1;
    const heading = Math.atan2(dirX, dirZ);
    const usable = Math.max(0, dist - PREMIUM_ROUTE.CHEVRON_START_DIST - PREMIUM_ROUTE.CHEVRON_END_PAD);
    const visibleCount = Math.min(
      PREMIUM_ROUTE.CHEVRON_COUNT,
      Math.max(0, Math.floor(usable / 18)),
    );
    const scroll = (time * 16) % 18;

    chevronMat.color.copy(extractionUnlocked ? cUnlocked : cActive).lerp(cLocked, extractionUnlocked ? 0 : 0.14);
    chevronMat.opacity = extractionUnlocked ? 0.58 : 0.44;

    for (let i = 0; i < chevrons.length; i++) {
      const mesh = chevrons[i]!;
      mesh.visible = i < visibleCount && dist > 70;
      if (!mesh.visible) continue;
      const along = PREMIUM_ROUTE.CHEVRON_START_DIST + scroll + i * 18;
      const sideWave = Math.sin(time * 2.2 + i * 0.75) * 0.45;
      mesh.position.set(
        playerX + dirX * along + Math.cos(heading) * sideWave,
        PREMIUM_ROUTE.CHEVRON_Y,
        playerZ + dirZ * along - Math.sin(heading) * sideWave,
      );
      mesh.rotation.z = -heading;
      const pulse = 0.86 + Math.sin(time * 4.4 - i * 0.42) * 0.12;
      mesh.scale.setScalar(pulse);
    }

    beam.position.x = extractionX;
    beam.position.z = extractionZ;
    beam.rotation.y += dt * 0.28;
    beamMat.color.copy(extractionUnlocked ? cUnlocked : cLocked);
    beamMat.opacity = extractionUnlocked ? 0.14 : 0.09;

    for (let i = 0; i < rings.length; i++) {
      const ring = rings[i]!;
      ring.position.x = extractionX;
      ring.position.z = extractionZ;
      ring.rotation.z += dt * (0.42 + i * 0.08);
      const s = 1 + Math.sin(time * 2.2 + i * 0.9) * 0.04;
      ring.scale.setScalar(s);
      const mat = ring.material as THREE.MeshBasicMaterial;
      mat.color.copy(extractionUnlocked ? cUnlocked : cLocked);
      mat.opacity = extractionUnlocked ? 0.34 : 0.22;
    }
  };

  return { group, update };
}
